import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '../../lib/icons';
import { KIND_ICON, STATUS_STYLE, progressFraction, etaLabel } from '../../lib/eta';
import { useActivityStore, type Activity, type ActivityLogEntry, MAX_LOG_LINES } from '../../lib/activityStore';
import { stopSyncRun } from '../../lib/api';
import { readerLocale } from '../../lib/locale';

/**
 * Reusable per-row renderer for the Activity Center — the single end to the old
 * duplication across SyncLogPanel / AnalysisPanel.
 *
 * Collapsed: kind glyph + title + status badge + determinate/indeterminate
 * progress bar + etaLabel + done/total + Stop (if stoppable) + ✕ dismiss + an
 * optional href Link. Expanded (click to toggle): the live log transcript + AI
 * streaming pane via <ActivityLiveDetail/> (auto-scroll, monospace, capped).
 */

const LOG_COLOR: Record<ActivityLogEntry['type'], string> = {
  info: 'var(--text-secondary)',
  success: 'var(--accent-success)',
  error: 'var(--accent-error)',
};

export function ActivityRow({ activity, onNavigate }: { activity: Activity; onNavigate?: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();
  const remove = useActivityStore((s) => s.remove);

  const style = STATUS_STYLE[activity.status] ?? STATUS_STYLE.running;
  const frac = progressFraction(activity);
  const eta = etaLabel(activity);
  const p = activity.progress;
  const running = activity.status === 'running';
  const hasDetail = (activity.logs?.length ?? 0) > 0 || !!activity.streamingContent;

  const onStop = async () => {
    if (!activity.serverRunId) return;
    try {
      await stopSyncRun(activity.serverRunId);
      queryClient.invalidateQueries({ queryKey: ['sync-runs'] });
    } catch { /* ignore */ }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      {/* Collapsed header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span style={{ fontSize: 15, flexShrink: 0, width: 18, textAlign: 'center' }} aria-hidden>
          {KIND_ICON[activity.kind] ?? '•'}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <button
            type="button"
            onClick={() => hasDetail && setExpanded((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)', width: '100%',
              background: 'transparent', border: 'none', padding: 0, textAlign: 'left',
              cursor: hasDetail ? 'pointer' : 'default', color: 'var(--text-primary)', minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
              }}
              title={activity.title}
            >
              {activity.title}
            </span>
            {hasDetail && (
              <Icon
                name="chevronDown"
                size={14}
                style={{ color: 'var(--text-tertiary)', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
              />
            )}
          </button>
          {(activity.message || activity.error) && (
            <div
              style={{
                fontSize: 'var(--text-xs)', color: activity.error ? 'var(--accent-error)' : 'var(--text-tertiary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              title={activity.error || activity.message}
            >
              {activity.error || activity.message}
            </div>
          )}
        </div>

        {/* Status badge */}
        <span
          style={{
            flexShrink: 0, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999,
            color: style.color, background: style.bg, textTransform: 'lowercase',
          }}
        >
          {style.label}
        </span>

        {/* Stop (running + stoppable) */}
        {running && activity.stoppable && (
          <button
            type="button"
            onClick={onStop}
            className="activity-row-stop"
            style={{
              flexShrink: 0, background: 'transparent', border: '1px solid var(--accent-error)',
              color: 'var(--accent-error)', borderRadius: 'var(--radius-sm)', fontSize: 11,
              padding: '2px 9px', cursor: 'pointer',
            }}
          >
            Stop
          </button>
        )}

        {/* Dismiss */}
        {!running && (
          <button
            type="button"
            aria-label="Dismiss"
            title="Dismiss"
            onClick={() => remove(activity.id)}
            className="btn-icon"
            style={{
              flexShrink: 0, background: 'transparent', border: 'none', color: 'var(--text-tertiary)',
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: 4,
            }}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {/* Progress bar — determinate when a denominator exists, else indeterminate */}
      {running && (
        <div
          className="activity-row-track"
          style={{ height: 3, borderRadius: 999, background: 'var(--bg-elevated)', overflow: 'hidden', position: 'relative' }}
        >
          {frac == null ? (
            <span
              className="activity-row-bar-indeterminate"
              style={{ position: 'absolute', inset: 0, background: 'var(--accent-primary)', opacity: 0.6 }}
            />
          ) : (
            <span
              style={{
                display: 'block', height: '100%', width: `${Math.round(frac * 100)}%`,
                background: 'var(--accent-primary)', transition: 'width 0.3s ease',
              }}
            />
          )}
        </div>
      )}

      {/* Meta line: done/total + ETA + href */}
      {(p || eta || activity.href) && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)',
          }}
        >
          {p && p.total > 0 && (
            <span>
              {p.done.toLocaleString(readerLocale())} / {p.total.toLocaleString(readerLocale())}
              {p.phase ? ` · ${p.phase}` : ''}
            </span>
          )}
          {eta && <span>{eta}</span>}
          <span style={{ flex: 1 }} />
          {activity.href && (
            <Link
              to={activity.href.to}
              onClick={onNavigate}
              style={{ color: 'var(--accent-primary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {activity.href.label}
              <Icon name="chevronRight" size={12} />
            </Link>
          )}
        </div>
      )}

      {/* Expanded live detail */}
      {expanded && hasDetail && <ActivityLiveDetail activity={activity} />}
    </div>
  );
}

/** Live log transcript + AI streaming pane. Lifted from AnalysisPanel's
 *  log+stream split: monospace, auto-scrolling, capped to the last N lines. */
export function ActivityLiveDetail({ activity }: { activity: Activity }) {
  const logRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<HTMLDivElement>(null);

  const logs = activity.logs ?? [];
  // Defensive cap (the store already caps on write).
  const shown = logs.length > MAX_LOG_LINES ? logs.slice(logs.length - MAX_LOG_LINES) : logs;
  const stream = activity.streamingContent;
  const running = activity.status === 'running';

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight;
  }, [activity.logs, activity.streamingContent]);

  const mono = "'SF Mono', 'Cascadia Code', 'Fira Code', monospace";

  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
        marginTop: 'var(--space-1)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border-subtle)', overflow: 'hidden', background: 'var(--bg-elevated)',
      }}
    >
      {shown.length > 0 && (
        <div
          ref={logRef}
          style={{
            maxHeight: 160, overflow: 'auto', padding: 'var(--space-2) var(--space-3)',
            fontFamily: mono, fontSize: 11, lineHeight: 1.6,
          }}
        >
          {shown.map((entry, i) => (
            <div key={i}>
              <span style={{ color: 'var(--text-tertiary)', marginRight: 6 }}>
                {new Date(entry.ts).toLocaleTimeString(readerLocale())}
              </span>
              <span style={{ color: LOG_COLOR[entry.type] }}>{entry.text}</span>
            </div>
          ))}
          {running && (
            <div style={{ color: 'var(--accent-primary)' }}>
              <span className="activity-detail-caret">▍</span>
            </div>
          )}
        </div>
      )}

      {stream != null && stream.length > 0 && (
        <div
          ref={streamRef}
          style={{
            maxHeight: 200, overflow: 'auto', padding: 'var(--space-2) var(--space-3)',
            fontFamily: mono, fontSize: 11, lineHeight: 1.6, color: 'var(--text-secondary)',
            whiteSpace: 'pre-wrap', borderTop: shown.length > 0 ? '1px solid var(--border-subtle)' : 'none',
            background: 'var(--bg-card)',
          }}
        >
          {stream}
        </div>
      )}
    </div>
  );
}
